import * as t from 'io-ts';

import {makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {hasProperty, makeKeyOfType} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {UserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {CompanyFeaturesEnum} from '../../models/api/companyModel';
import {PermissionsEnum} from '../../models/api/roleModel';
import {TeamModel} from '../../models/api/teamModel';
import {FeatureFlagsEnum} from '../../store/featureFlags/featureFlagsTypes';
import {RSettingsPathId, SettingsPathId} from './settingsHelpers';

export interface SettingsNavItemCommonStyleProps {
  $theme: UserTheme;
}

export enum SettingsMenuItemRequirementTypesEnum {
  ADMIN = 'admin',
  BILLING = 'billing',
  DELIVERABILITY = 'deliverability',
  MULTIPLE_TEAMS = 'multiple-teams',
  NOT_ADMIN = 'not-admin',
  SINGLE_TEAM = 'single-team',
  TEAM_ADMIN = 'team-admin',
}

export const isSettingsMenuItemRequirementTypesEnum = makeEnumTypeGuard(SettingsMenuItemRequirementTypesEnum);

const RSettingsMenuItemParameters = t.partial({
  namespace: t.string,
  tabpage: t.string,
});

const RSettingsMenuItemParametersKey = makeKeyOfType(
  'SettingsMenuItemParametersKey',
  RSettingsMenuItemParameters.props,
);

export const RSettingsMenuItemTemplate = t.intersection([
  t.type({
    pathId: RSettingsPathId,
  }),
  t.partial({
    parameters: RSettingsMenuItemParameters,
    omittedParametersForActiveStatus: t.readonlyArray(RSettingsMenuItemParametersKey),
  }),
]);

export interface SettingsMenuItemTemplate extends t.TypeOf<typeof RSettingsMenuItemTemplate> {}

export type SettingsMenuItemCondition =
  | SettingsMenuItemRequirementTypesEnum
  | PermissionsEnum
  | CompanyFeaturesEnum
  | FeatureFlagsEnum;

interface SettingsMenuItemBase extends SettingsMenuItemTemplate {
  name: string;
  hasUpsell?: boolean;
}

interface SettingsMenuItemWithNoConditions extends SettingsMenuItemBase {
  showConditions?: never;
  hideConditions?: never;
}
interface SettingsMenuItemWithShowConditions extends SettingsMenuItemBase {
  /** Conditions the item must pass in order to be shown. */
  showConditions: ReadonlyArray<SettingsMenuItemCondition>;
  hideConditions?: never;
}
interface SettingsMenuItemWithHideConditions extends SettingsMenuItemBase {
  /** Item gets hidden if all conditions are true. */
  hideConditions: ReadonlyArray<FeatureFlagsEnum>;
  showConditions?: never;
}
export type SettingsMenuItem =
  | SettingsMenuItemWithNoConditions
  | SettingsMenuItemWithShowConditions
  | SettingsMenuItemWithHideConditions;

export function isSettingsItemWithShowConditions(
  item: SettingsMenuItemBase,
): item is SettingsMenuItemWithShowConditions {
  return hasProperty(item, 'showConditions');
}
export function isSettingsItemWithHideConditions(
  item: SettingsMenuItemBase,
): item is SettingsMenuItemWithHideConditions {
  return hasProperty(item, 'hideConditions');
}

export enum SettingsMenuBlockTypesEnum {
  HOME = 'HOME',
  SECTION = 'SECTION',
}

export interface SettingsMenuHomeBlock {
  type: SettingsMenuBlockTypesEnum.HOME;
  title: string;
  pathId: SettingsPathId;
  icon?: IconName;
}

export enum SettingsMenuSectionTypesEnum {
  COMPANY,
  PERSONAL,
  TEAM,
}

export interface SettingsMenuItemsBlock {
  type: SettingsMenuBlockTypesEnum.SECTION;
  title: string;
  sectionId: string;
  sectionType: SettingsMenuSectionTypesEnum;
  items: ReadonlyArray<SettingsMenuItem>;
  team?: TeamModel;
}

export type SettingsMenuBlock = SettingsMenuHomeBlock | SettingsMenuItemsBlock;

export interface SettingsMenuDefinition {
  personal: ReadonlyArray<SettingsMenuBlock>;
  company: ReadonlyArray<SettingsMenuBlock>;
}
